import { DEFAULT_PHONE_COUNTRY, normalizePhoneToE164 } from './phone.js';

/** Keys promoted to Lead columns or lead-gate — hide from questionnaire extras. */
export const LEAD_CORE_PROMOTED_KEYS = [
  'email',
  'phone',
  'firstName',
  'lastName',
  'name',
  'consent',
] as const;

export type LeadCorePromotedKey = (typeof LEAD_CORE_PROMOTED_KEYS)[number];

export type LeadCoreFields = {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  consentGiven: boolean;
};

export type ExtractLeadCoreInput = {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  consentGiven?: boolean;
  answers?: Record<string, unknown> | null;
};

function normStr(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t.length > 0 ? t : null;
}

function splitFullName(full: string): { firstName: string | null; lastName: string | null } {
  const t = full.trim();
  if (!t) return { firstName: null, lastName: null };
  const space = t.indexOf(' ');
  if (space <= 0) return { firstName: t, lastName: null };
  return {
    firstName: t.slice(0, space).trim() || null,
    lastName: t.slice(space + 1).trim() || null,
  };
}

/**
 * Resolve core identity fields from explicit DTO values and/or flat survey answers.
 * Priority: explicit firstName/lastName → answers.firstName/lastName → split name.
 */
export function extractLeadCore(input: ExtractLeadCoreInput): LeadCoreFields {
  const answers = input.answers ?? {};
  let firstName = normStr(input.firstName ?? answers.firstName);
  let lastName = normStr(input.lastName ?? answers.lastName);

  if (!firstName && !lastName) {
    const fromName = splitFullName(normStr(answers.name) ?? '');
    firstName = fromName.firstName;
    lastName = fromName.lastName;
  }

  const email = normStr(input.email ?? answers.email)?.toLowerCase() ?? null;
  const rawPhone = normStr(input.phone ?? answers.phone);
  let phone: string | null = null;
  if (rawPhone) {
    const parsed = normalizePhoneToE164(rawPhone, DEFAULT_PHONE_COUNTRY);
    phone = 'e164' in parsed ? parsed.e164 : rawPhone;
  }
  const consentRaw = input.consentGiven ?? answers.consent;
  const consentGiven = consentRaw === true || consentRaw === 'true' || consentRaw === 1;

  return { firstName, lastName, email, phone, consentGiven };
}

export function formatLeadDisplayName(lead: {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}): string {
  const name = `${lead.firstName ?? ''} ${lead.lastName ?? ''}`.trim();
  if (name) return name;
  return lead.email?.trim() ?? '—';
}

export function isLeadCorePromotedKey(key: string): key is LeadCorePromotedKey {
  return (LEAD_CORE_PROMOTED_KEYS as readonly string[]).includes(key);
}
